<script lang="ts">
	// Svelte
	import { createEventDispatcher } from 'svelte';
	// Components
	import { DropdownMenu, MenuItemList } from '@Shelly/shelly-design-system';
	import OptionManagmentModal from './OptionManagmentModal.svelte';
	import TabsActionsButton from './TabsActionsButton.svelte';
	// Helpeprs
	import { format } from 'svelte-i18n';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { buildDropdownMenus, menuCommandsToDropdownItems } from './Scenes/helpers/menuBuilders';
	import { user_data } from 'src/stores/account/user';
	import ChooseAIModalModal from './ChooseAIModalModal.svelte';
	import { resetFiltersAllDevicesList } from 'src/stores/ui/filters';
	import {
		bulkActionEdit,
		edit,
		update,
		type UpdateModeSubTypes,
		type UpdateModeTypes,
	} from 'src/stores/ui/edit_lists';
	import { firstCharCapOnly } from 'src/helpers/utilities';
	import type { IconButtonPropsTypes } from '@Shelly/shelly-design-system';

	export let context: 'room' = 'room';
	export let additionalDropdownGroup = null;
	export let addBulkCommandsOptions = false;
	export let areFiltersDisabled = false;
	export let disableBulkActions = false;
	export let disableReorderButton = false;
	/**
	 * Optional extra section rendered after the room actions + bulk actions
	 * groups. Used by the room-scoped device list to fold "Change view" into
	 * this kebab instead of showing a separate segmented control.
	 * Shape: `{ groupTitle, groupTitleDefault, items: [{title, titleDefault, icon, disabled, onClick}] }`.
	 */
	export let viewSelectionGroup: {
		groupTitle: string;
		groupTitleDefault: string;
		isGroupTitle?: boolean;
		items: Array<{
			id: number;
			title: string;
			titleDefault: string;
			icon: string;
			disabled?: boolean;
			onClick?: () => void;
		}>;
	} | null = null;
	/**
	 * Trigger IconButton variant. Defaults to `secondary` for toolbar usage;
	 * pass `help-2` when the kebab lives inside the tabs action slot so it
	 * matches the transparent style used by the dashboard kebab.
	 */
	export let triggerVariant: IconButtonPropsTypes['variant'] = 'secondary';

	const dispatch = createEventDispatcher();
	const dispatchSubmit = createEventDispatcher<{ 'submit-click': {} }>();

	let windowClientWidth: number;
	let showDrodownManageOptions = false; // State for showing dropdown for icon button when context !== 'general'
	let showModalManageOptions = false; // State for showing modal for icon button when context !== 'general'
	let showAIModelCommandsModal = false;
	let sharedBulkDropdownItems: any[] = [];

	const AIModelCommands = [
		{
			name: 'text_enable_ai_model',
			id: 'enable_ai_model' as UpdateModeSubTypes,
			icon: 'fa-light fa-toggle-on text-success',
		},
		{
			name: 'text_disable_ai_model',
			id: 'disable_ai_model' as UpdateModeSubTypes,
			icon: 'fa-light fa-toggle-off text-danger',
		},
		{
			name: 'manage_ai_notifications',
			id: 'manage_ai_notifications' as UpdateModeSubTypes,
			icon: 'fa-light fa-bell',
		},
	];

	$: {
		const { bulkCommands } = buildDropdownMenus({
			format: $format,
			userData: $user_data,
			setShowDropdownMenu: () => {},
			setShowAIModelCommandsModal: (v) => (showAIModelCommandsModal = v),
			dispatchSubmit: () => dispatchSubmit('submit-click'),
			onChangeClick: onChangeClick,
			updateStore: updateStore,
			newLayout: true,
		});

		sharedBulkDropdownItems = menuCommandsToDropdownItems(
			bulkCommands,
			{ idOffset: 100 },
			disableBulkActions,
		);
	}

	$: mergedAdditionalDropdownGroup = (() => {
		if (!addBulkCommandsOptions) {
			if (additionalDropdownGroup?.items) {
				return {
					...additionalDropdownGroup,
					items: additionalDropdownGroup.items.map((item) =>
						item.title === 'reorder_room_items_title'
							? { ...item, disabled: disableReorderButton }
							: item,
					),
				};
			}
			return additionalDropdownGroup;
		}

		if (additionalDropdownGroup) {
			return {
				...additionalDropdownGroup,
				items: [
					...(additionalDropdownGroup.items?.map((item) =>
						item.title === 'reorder_room_items_title'
							? { ...item, disabled: disableReorderButton }
							: item,
					) ?? []),
					...sharedBulkDropdownItems,
				],
			};
		}

		return {
			groupTitleDefault: 'Device Actions',
			groupTitle: 'device_actions_group_title',
			isGroupTitle: true,
			items: sharedBulkDropdownItems,
		};
	})();

	$: dropdownData = [
		{
			groupTitleDefault: `Manage ${context}`,
			groupTitle: `menage_${context}_group_title`,
			isGroupTitle: true,
			items: [
				{
					id: 1,
					titleDefault: `Edit ${context}`,
					title: `prompt_title_edit_${context}`,
					icon: 'fas fa-pen',
				},
				{
					id: 2,
					titleDefault: `Delete ${context}`,
					title: `button_delete_${context}`,
					icon: 'text-danger fa-solid fa-trash',
					isSelectable: false,
					titleClasses: 'text-danger',
				},
				{
					titleDefault: 'Reorder room items',
					title: 'reorder_room_items_title',
					icon: 'fas fa-arrows-up-down-left-right',
					id: 3,
					disabled: disableBulkActions,
				},
			],
		},
		mergedAdditionalDropdownGroup,
		viewSelectionGroup,
	].filter((item) => item !== null);

	const onToggleDashActions = () => {
		if (isTabletOrMobile) {
			showModalManageOptions = !showModalManageOptions;
		} else {
			showDrodownManageOptions = !showDrodownManageOptions;
		}
	};

	const updateStore = (
		type: UpdateModeTypes,
		subType: UpdateModeSubTypes = '' as UpdateModeSubTypes,
	) => {
		bulkActionEdit.set(true);
		areFiltersDisabled = true;
		update.update((s) => {
			return {
				...s,
				value: true,
				type: type,
				subType: subType,
			};
		});
		edit.set(true);
	};

	const onChangeClick = (type: UpdateModeTypes, subType = '' as UpdateModeSubTypes) => {
		updateStore(type, subType);
		resetFiltersAllDevicesList();
	};

	const clearSubmenuStates = () => {
		dropdownData.forEach((group) => {
			group.items?.forEach((item) => {
				if (item?.hasOwnProperty('showDropdown')) {
					item.showDropdown = false;
					item.active = false;
				}
			});
		});

		dropdownData = dropdownData;
	};

	$: isTabletOrMobile = windowClientWidth <= SystemScreenBreakpoints.TABLET_MAX;
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

<DropdownMenu bind:show={showDrodownManageOptions}>
	<TabsActionsButton
		slot="trigger"
		ariaLabel="Open {context} actions"
		variant={triggerVariant}
		isActive={showDrodownManageOptions}
		on:click={onToggleDashActions}
	/>
	{#each dropdownData as dropdownAction}
		<MenuItemList
			title={$format(dropdownAction.groupTitle, {
				default: `${dropdownAction.groupTitleDefault}`,
			})}
			isGroupTitle={dropdownAction.isGroupTitle}
		/>
		{#each dropdownAction.items as dropdownMenuItem, i}
			{#if dropdownMenuItem.hasOwnProperty('showDropdown')}
				<DropdownMenu
					class="width-full"
					bind:show={dropdownMenuItem.showDropdown}
					positionHorizontally
					on:close={() => {
						dropdownAction.items[i].active = false;
					}}
				>
					<MenuItemList
						isGroupItem
						hasBorder
						title={dropdownMenuItem.title ===
						$format('sub_settings_title_ai_model', { default: 'AI Model' })
							? dropdownMenuItem.title
							: firstCharCapOnly(dropdownMenuItem.title)}
						icon={dropdownMenuItem.icon}
						rightIcon={dropdownMenuItem.rightIcon}
						active={dropdownMenuItem.active}
						slot="trigger"
						on:mouseenter={() => {
							dropdownAction.items[i].showDropdown = true;
							dropdownAction.items[i].active = true;
						}}
						on:click={() => {
							if (windowClientWidth <= SystemScreenBreakpoints.MOBILE_MAX) {
								dropdownMenuItem.showDropdown = !dropdownMenuItem.showDropdown;
							}
						}}
					/>

					{#each dropdownMenuItem.items as submenuItem}
						<MenuItemList
							title={submenuItem.title}
							icon={submenuItem.icon}
							on:click={() => {
								if (
									submenuItem.id === 9 ||
									submenuItem.id === 10 ||
									submenuItem.id === 11
								) {
									onChangeClick('ai_device_failure', submenuItem.aiSubtype);
								}
								clearSubmenuStates();
								dropdownAction.items[i].showDropdown = false;
								dropdownAction.items[i].active = false;
							}}
						/>
					{/each}
				</DropdownMenu>
			{:else}
				<MenuItemList
					isGroupItem
					title={$format(dropdownMenuItem.title, {
						default: `${dropdownMenuItem.titleDefault}`,
					}) || ''}
					titleClasses={dropdownMenuItem.titleClasses}
					subtitle={dropdownMenuItem.subtitle}
					avatar={dropdownMenuItem.avatar}
					icon={dropdownMenuItem.icon}
					disabled={dropdownMenuItem.disabled}
					on:mouseenter={() => {
						clearSubmenuStates();
					}}
					on:click={() => {
						if (dropdownMenuItem.onClick) {
							dropdownMenuItem.onClick();
						} else if (dropdownMenuItem.id === 1) {
							dispatch('edit-click', dropdownMenuItem);
						} else if (dropdownMenuItem.id === 2) {
							dispatch('delete-click', dropdownMenuItem);
						} else {
							dispatch('additional-dropdown-item-click', dropdownMenuItem);
						}

						onToggleDashActions();
					}}
				/>
			{/if}
		{/each}
	{/each}
</DropdownMenu>

<OptionManagmentModal
	bind:showModal={showModalManageOptions}
	topSectionTitle={$format(`menage_${context}_group_title`, { default: `Manage ${context}` })}
	bottomSectionTitle={$format(mergedAdditionalDropdownGroup?.groupTitle, {
		default: mergedAdditionalDropdownGroup?.groupTitleDefault,
	})}
	extraSectionTitle={viewSelectionGroup
		? $format(viewSelectionGroup.groupTitle, { default: viewSelectionGroup.groupTitleDefault })
		: ''}
	itemsTop={[
		{
			id: 1,
			titleDefault: `Edit ${context}`,
			title: `prompt_title_edit_${context}`,
			icon: 'fal fa-pen',
		},
		{
			id: 2,
			titleDefault: `Delete ${context}`,
			title: `button_delete_${context}`,
			icon: 'text-danger fal fa-trash',
			isSelectable: false,
			titleClasses: 'text-danger',
		},
		{
			titleDefault: 'Reorder room items',
			title: 'reorder_room_items_title',
			icon: 'fal fa-arrows-up-down-left-right',
			id: 3,
			disabled: disableBulkActions,
		},
	]}
	itemsBottom={mergedAdditionalDropdownGroup?.items.map((item) => ({
		...item,
		icon: item.icon
			?.replace(/\b(fas|far|fal|fat|fad)\b/g, 'fa-light')
			.replace(/\bfa-(solid|regular|light|thin|duotone)\b/g, 'fa-light'),
	}))}
	itemsExtra={viewSelectionGroup?.items ?? []}
	{AIModelCommands}
	on:click={(e) => {
		if (e.detail.id === 1) {
			dispatch('edit-click', e.detail);
		} else if (e.detail.id === 2) {
			dispatch('delete-click', e.detail);
		} else {
			dispatch('additional-dropdown-item-click', e.detail);

			onToggleDashActions();
		}
	}}
	on:ai-command-click={(e) => {
		onChangeClick(e.detail.type, e.detail.subType);
	}}
/>

<ChooseAIModalModal bind:showAIModelCommandsModal {onChangeClick} />
